/**
 * Focus-trapped dialog — the base for the builder/billing/settings surfaces.
 * Renders through a Portal, traps Tab focus, closes on Escape or scrim click,
 * and restores focus on unmount (via `trapFocus`). Token-driven, reduced-motion
 * honored. `labelledBy`/`describedBy` wire the accessible name.
 */
import type { JSX } from 'solid-js';
import { onCleanup, Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import { trapFocus } from '../focus/trap-focus.ts';
import { ensureBaseStyles } from '../internal/base-styles.ts';
import { cx } from '../internal/cx.ts';
import { ensureStyles } from '../internal/inject-styles.ts';

const STYLE_ID = 'f4-modal';
const CSS = `
.f4-scrim {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4);
  z-index: var(--z-modal, 400);
}
.f4-scrim__backdrop {
  position: absolute;
  inset: 0;
  border: none;
  padding: 0;
  background: var(--color-scrim);
  cursor: pointer;
  animation: f4-fade-in var(--duration-fast, 150ms) var(--ease-standard, ease);
}
.f4-modal {
  position: relative;
  width: 100%;
  max-width: 32rem;
  max-height: 90vh;
  overflow: auto;
  padding: var(--space-5);
  background: var(--color-surface-overlay);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--elevation-4);
  animation: f4-scale-in var(--duration-base, 240ms) var(--ease-spring, ease);
}
@media (prefers-reduced-motion: reduce) {
  .f4-scrim, .f4-modal { animation: none; }
}
`;

export interface ModalProps {
  /** Whether the dialog is shown. */
  readonly open: boolean;
  /** Requested close (Escape, scrim click). */
  readonly onClose: () => void;
  /** id of the element labelling the dialog (e.g. the title). */
  readonly labelledBy?: string;
  /** id of the element describing the dialog. */
  readonly describedBy?: string;
  /** Accessible label for the backdrop close button (no bare literal). */
  readonly closeLabel: string;
  readonly class?: string;
  readonly children?: JSX.Element;
}

export function Modal(props: ModalProps): JSX.Element {
  ensureBaseStyles();
  ensureStyles(STYLE_ID, CSS);

  let dispose: (() => void) | undefined;
  const attach = (node: HTMLElement): void => {
    dispose = trapFocus(node, props.onClose);
  };
  onCleanup(() => dispose?.());

  return (
    <Show when={props.open}>
      <Portal>
        <div class="f4-scrim">
          {/* Backdrop is a real button so click-to-close is keyboard-reachable;
              Escape is also handled by the focus trap. */}
          <button
            type="button"
            class="f4-scrim__backdrop"
            aria-label={props.closeLabel}
            onClick={() => props.onClose()}
          />
          <div
            ref={attach}
            role="dialog"
            aria-modal="true"
            aria-labelledby={props.labelledBy}
            aria-describedby={props.describedBy}
            tabindex="-1"
            class={cx('f4-modal', 'f4-focus-ring', props.class)}
          >
            {props.children}
          </div>
        </div>
      </Portal>
    </Show>
  );
}
